<template>
  <div class="shenyunjie-chart-wind" :id="id"></div>
</template>

<script>
import { parseTime } from "../../utils/index.js";
import * as echarts from "echarts";
import merge from "deepmerge";

export default {
  name: "YunJieChartWind",
  props: {
    id: {
      type: String,
      default: function () {
        return new Date().getTime() + "-" + Math.ceil(Math.random() * 1000);
      }
    },
    loadChart: {
      type: Boolean,
      default: true
    },
    theme: {
      type: String,
      default: ""
    },
    title: {
      type: String,
      default: ""
    },
    xData: {
      type: Array,
      default: function () {
        return [];
      }
    },
    yData: {
      type: Array,
      default: function () {
        return [];
      }
    },
    unit: {
      type: String,
      default: ""
    },
    grade: {
      type: Boolean,
      default: true
    },
    alarm: {
      type: Boolean,
      default: true
    },
    originNorth: {
      type: Number,
      default: 0
    },
    direction: {
      type: Boolean,
      default: false
    },
    directionIcon: {
      type: Boolean,
      default: true
    },
    tooltip: {
      type: Boolean,
      default: true
    },
    dataZoom: {
      type: Boolean,
      default: true
    },
    option: {
      type: Object,
      default: function () {
        return {}
      }
    },
    updateOption: {
      type: Function,
      default: null
    }
  },
  components: {},
  data() {
    return {
      chart: null,
      defaultOption: {
        grid: {
          left: 72,
          right: 24
        },
        title: {
          left: "center"
        },
        xAxis: {
          type: "time",
          axisLabel: {
            formatter: "{yyyy}-{MM}-{dd} {HH}:{mm}:{ss}"
          }
        }
      },
      resizeObserver: null
    };
  },
  mounted() {
    let self = this;
    // 窗口大小变化监听
    window.addEventListener("resize", self.resizeWind);
    // 元素大小变化监听
    self.resizeObserver = new ResizeObserver((entries) => {
      if (self.chart) {
        self.chart.dispose();
        this.chart = null;
      }
      self.init();
    });
    self.resizeObserver.observe(document.getElementById(self.id));
    if (self.loadChart) {
      self.init();
    }
  },
  methods: {
    resizeWind() {
      let self = this;
      if (self.chart) {
        self.chart.resize();
      }
    },
    init() {
      let self = this;
      // 页面已存在
      if (document.getElementById(self.id) && document.getElementById(self.id).offsetHeight > 0) {
        // 初始化
        if (!self.chart) {
          let theme = self.theme || (this.$YunJie || {}).theme || "standard";
          if (theme == "darkness") {
            self.chart = echarts.init(document.getElementById(self.id), "dark");
          } else {
            self.chart = echarts.init(document.getElementById(self.id));
          }
        }
        let data = [];
        for (let i = 0; i < self.xData.length; i++) {
          data.push([self.xData[i], self.yData[i][0], self.yData[i][1]]);
        }
        // 配置汇总
        let parameter = {
          grid: {
            top: self.title ? 32 : 16,
            bottom: self.dataZoom ? 70 : 32
          },
          title: {
            text: self.title
          },
          yAxis: {
            name: "风速",
            nameLocation: "middle",
            nameGap: 46
          },
          tooltip: {
            show: self.tooltip,
            trigger: "axis",
            formatter: function (params) {
              let str = `${parseTime(new Date(params[0].axisValue), "{yyyy}-{MM}-{dd} {HH}:{mm}:{ss}")}<br/>`;
              if (self.grade) {
                str += self.getGrade(params[0].data[1]);
              }
              if (self.alarm) {
                str += self.getAlarm(params[0].data[1]);
              }
              str += `${params[0].marker} ${params[0].seriesName}：${params[0].data[1]}${self.unit}`;
              str += "<br/>";
              let marker = self.directionIcon ? params[1].marker : '<span style="display: inline-block; margin-right: 4px; border-radius: 10px; width: 10px; height: 10px; background-color: #91cc75;"></span>';
              let name = self.directionIcon ? params[1].seriesName : "风向";
              let value = (params[0].data[2] - self.originNorth) % 360;
              if (self.direction) {
                value = self.getDirection(value);
              }
              str += `${marker} ${name}：${value}${self.direction ? "" : "°"}`;
              return str;
            }
          },
          dataZoom: self.dataZoom ? [{
            type: "inside"
          }, {
            type: "slider"
          }] : [],
          series: [{
            name: "风速",
            type: "line",
            symbol: "none",
            encode: {
              x: 0,
              y: 1
            },
            lineStyle: {
              type: self.directionIcon ? "dotted" : "solid"
            },
            data: data
          }]
        };
        let opt = merge(self.defaultOption, parameter);
        if (self.directionIcon) {
          opt.series.push({
            name: "风向",
            type: "custom",
            renderItem: function (param, api) {
              let value = api.value(2) - self.originNorth;
              return {
                type: "path",
                shape: {
                  pathData: 'M 0 0 L 70 -40 L 140 0 L 70 -140 z',
                  x: -7,
                  y: -7,
                  width: 14,
                  height: 14
                },
                rotation: -value * Math.PI / 180,
                position: api.coord([
                  api.value(0),
                  api.value(1)
                ]),
                style: api.style({
                  stroke: "#18BF12"
                })
              }
            },
            encode: {
              x: 0,
              y: 1
            },
            data: data,
            z: 5
          });
        }
        if (self.alarm) {
          opt.visualMap = {
            type: "piecewise",
            show: false,
            pieces: [{
              gt: 37.0,
              color: "#CD3338",
            }, {
              gt: 32.7,
              lte: 37.0,
              color: "#FC9706",
            }, {
              gt: 24.5,
              lte: 32.7,
              color: "#FEFE08",
            }, {
              gt: 17.2,
              lte: 24.5,
              color: "#2E67F4",
            }, {
              lt: 17.2,
              color: "#18BF12"
            }],
            dimension: 1
          };
        }
        // 合并自定义配置
        opt = merge(opt, self.option);
        // 用户自定义属性
        if (self.updateOption) {
          opt = self.updateOption(opt);
        }
        self.chart.setOption(opt, true);
        self.chart.resize();
      }
    },
    getAlarm(v) {
      let alarm = "";
      let color = "";
      if (v > 37) {
        alarm = "红色预警";
        color = "#CD3338";
      } else if (v > 32.7) {
        alarm = "橙色预警";
        color = "#FC9706";
      } else if (v > 24.5) {
        alarm = "黄色预警";
        color = "#FEFE08";
      } else if (v > 17.2) {
        alarm = "蓝色预警";
        color = "#2E67F4";
      } else {
        return "";
      }
      return `<span style="color: ${color};">${alarm}</span><br/>`;
    },
    getGrade(v) {
      let grade = "";
      if (v < 0.3) {
        grade = "0级(无风)";
      } else if (v < 1.6) {
        grade = "1级(软风)";
      } else if (v < 3.4) {
        grade = "2级(轻风)";
      } else if (v < 5.5) {
        grade = "3级(微风)";
      } else if (v < 8.0) {
        grade = "4级(和风)";
      } else if (v < 10.8) {
        grade = "5级(劲风)";
      } else if (v < 13.9) {
        grade = "6级(强风)";
      } else if (v < 17.2) {
        grade = "7级(疾风)";
      } else if (v < 20.8) {
        grade = "8级(大风)";
      } else if (v < 24.5) {
        grade = "9级(烈风)";
      } else if (v < 28.5) {
        grade = "10级(狂风)";
      } else if (v < 32.7) {
        grade = "11级(暴风)";
      } else {
        grade = "12级(飓风)及以上";
      }
      return `${grade}<br/>`;
    },
    getDirection(v) {
      let direction = "";
      if (v < 11.25) {
        direction = "N";
      } else if (v < 33.75) {
        direction = "NNE";
      } else if (v < 56.25) {
        direction = "NE";
      } else if (v < 78.75) {
        direction = "ENE";
      } else if (v < 101.25) {
        direction = "E";
      } else if (v < 123.75) {
        direction = "ESE";
      } else if (v < 146.25) {
        direction = "SE";
      } else if (v < 168.75) {
        direction = "SSE";
      } else if (v < 191.25) {
        direction = "S";
      } else if (v < 213.75) {
        direction = "SSW";
      } else if (v < 236.25) {
        direction = "SW";
      } else if (v < 258.75) {
        direction = "WSW";
      } else if (v < 281.25) {
        direction = "W";
      } else if (v < 303.75) {
        direction = "WNW";
      } else if (v < 326.25) {
        direction = "NW";
      } else if (v < 348.75) {
        direction = "NNW";
      } else {
        direction = "N";
      }
      return direction;
    }
  },
  destroyed() {
    window.removeEventListener("resize", this.resizeWind);
    this.resizeObserver.disconnect();
  },
  watch: {
    theme() {
      // 销毁已有曲线
      if (this.chart) {
        this.chart.dispose();
        this.chart = null;
      }
      this.init();
    },
    title() {
      this.init();
    },
    xData: {
      deep: true,
      handler(xData) {
        this.init();
      }
    },
    yData: {
      deep: true,
      handler(yData) {
        this.init();
      }
    },
    unit() {
      this.init();
    },
    grade() {
      this.init();
    },
    alarm() {
      this.init();
    },
    originNorth() {
      this.init();
    },
    direction() {
      this.init();
    },
    directionIcon() {
      this.init();
    },
    tooltip() {
      this.init();
    },
    dataZoom() {
      this.init();
    },
    option: {
      deep: true,
      handler(option) {
        this.init();
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.shenyunjie-chart-wind {
  height: 100%;
  width: 100%;
}
</style>


// WEBPACK FOOTER //
// src/components/charts/wind.vue